import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  faChevronLeft,
  faDollar,
  faSave,
  faTrash,
  faUserPlus,
  faBookmark,
  faBook,
} from '@fortawesome/free-solid-svg-icons';
import { Messages } from 'primereact/messages';
import { PagesPaths } from '@/enums';
import { Button, Container, DropdownField, LoadingSpinner } from '@/components';
import { Depo20FormData } from '@/types/depo20';
import { EstadoType } from '@/types/importFCL';
import { useMessage, useToastNotification } from '@/hooks';

import { ImportFCLData } from '@/types/tableImportFCL';
import { queryClient } from '@/providers/AppProvider';
import { optionTemplate } from '@/layout/Header/Header';
import { deleteSettlementByOID } from '@/services/deletedMySettlement';
import { getParametroTexto } from '@/services/getParametroTexto';
import { getAccessToken } from '@/utils/auth';
import { decodeJWT } from '@/utils/decodeJWT';

import styles from './ImportFCL.module.scss';

import './ImportFCL.scss';
import { getDepo20List } from '../Depo20/services/getDepo20List';
import ImportFCLClientForm, { ClientState } from './ImportFCLClientForm';
import ImportFCLTable from './ImportFCLTable';
import ToolBar from './ToolBar/ToolBar';
import { getImportFCLTable } from '../Depo20/services/getImportFCLTable';
import ScheduleModalImportFCL from './ScheduleModalImportFCL';
import { scheduleImportFCL } from '../Depo20/services/scheduleImportFCL';
import {
  saveAndAddSettlement,
  SaveSettlementValidated,
  LiquidacionWebTipo,
} from '../Depo20/services/saveAndAddSettlement';
import ModalDetailPayment from '../Depo20/components/ModalDetailPayment';
import { getLiquidateContainerBl } from '../Depo20/services/getLiquidateContainerBl';
import { getLiquidateContainerBlExport } from '../Depo20/services/getLiquidateContainerBlExport';
import { addContainerBlVacioImport } from './services/addContainerBlVacioImport';
import { getOidVisitaAbierta } from './services/getOidVisitaAbierta';
import { verifyRut } from '../CustomerRegistration/verifyRut';
import { titularRut } from '../CustomerRegistration/titularRut';
import DeleteImportFCL from './DeleteImportFCL';
import { comprobarDocumento } from '../Depo20/services/comprobarDocumento';

interface IDeFCLProps {
  tipo: 'Import' | 'Export' | 'VacioImportacion';
}

const statusColors: { [key in EstadoType]: string } = {
  'En Proceso': '#374f93',
  'En Edición': '#ffd389',
  'Pendiente de Pago': '#fd8800',
  Agendada: '#4780bf',
  Paga: '#619b77',
  Solicitada: '#91c4fc',
  Validada: '#cbfcde',
  Rechazada: '#e24b39',
  Autorizada: '#7dd19d',
  defaultColor: 'white',
};
export const notDeletableStates = ['Paga', 'Facturada', 'Agendada'];

const getPageTitle = (
  id: number,
  tipo: string,
  isImport: boolean,
  t: TFunction
) => {
  switch (true) {
    case id && tipo === 'VacioImportacion':
      return t('mySettlement:editEmptyImport');
    case id && tipo !== 'VacioImportacion':
      return t(isImport ? 'settlementTitle' : 'settlementTitleExport');
    case !id && tipo === 'VacioImportacion':
      return t('mySettlement:newEmptyImport');
    case !id && tipo !== 'VacioImportacion':
      return t(isImport ? 'newSettlementTitle' : 'newExportSettlementTitle');
    default:
      return '';
  }
};

const ImportFCL: React.FC<IDeFCLProps> = ({ tipo }) => {
  const { t } = useTranslation(['importFCL', 'exportFCL', 'mySettlement']);
  const { showToast } = useToastNotification();
  const [params] = useSearchParams();
  const isImport = tipo === 'Import';

  const [selectedItems, setSelectedItems] = useState<ImportFCLData[]>([]);
  const [id, setId] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [clientData, setClientData] = useState<ClientState>();
  const [despachanteData, setDespachanteData] = useState<ClientState>();
  const [isPaymentDialogShown, setIsPaymentDialogShown] = useState(false);
  const [addingContainer, setAddingContainer] = useState(false);
  const [isZeroCostDialogOpen, setIsZeroCostDialogOpen] = useState(false);
  const { showMessage, elementRef, clearMessages } = useMessage();

  const toggleIsPaymentDialog = useCallback(() => {
    setIsPaymentDialogShown((prevState) => !prevState);
  }, [setIsPaymentDialogShown]);

  const handleZeroCostDialogChange = useCallback((isOpen: boolean) => {
    setIsZeroCostDialogOpen(isOpen);
  }, []);

  const showMessageError = useCallback(
    (message: string, title?: string) => {
      showMessage({
        severity: 'error',
        detail: message,
        summary: title,
      });
    },
    [showMessage]
  );

  const calculateAmounts = useCallback(
    async (
      dataArray: Array<{
        oidVisita: number;
        entregaDirecta: boolean;
        fechaRetiro: string | null;
        fechaIngreso: string | null;
        lateArrival: boolean;
      }>,
      itemsArray: ImportFCLData[]
    ) => {
      const results = [];
      let warningFound = false;

      await Promise.all(
        dataArray.map(async (data, index) => {
          const {
            oidVisita,
            entregaDirecta,
            fechaRetiro,
            fechaIngreso,
            lateArrival,
          } = data;

          const item = itemsArray[index];

          const body = {
            oidVisita,
            fechaRetiro: fechaRetiro ?? '',
            entregaDirecta,
            rut: '',
          };
          const bodyExport = {
            oidVisita: data?.oidVisita ?? 0,
            fecha: fechaIngreso ?? '',
            ingresoTardio: lateArrival,
            rut: '',
          };

          let res;
          if (tipo === 'VacioImportacion') {
            const marcaNumero = item.marcaNumero || '';
            const marca = marcaNumero.substring(0, 4);
            const numero = marcaNumero.substring(4).replace(/[/-]/g, '');

            const visitaData = await getOidVisitaAbierta({ marca, numero });

            const accessToken = getAccessToken();
            const decodedToken = accessToken ? decodeJWT(accessToken) : null;
            const rutTitular =
              decodedToken?.ultimo_rut_cliente_liquidacion_electronica_vacio_import ||
              '';

            const visita = {
              numero: visitaData.numero,
              marca: visitaData.marca,
              isoCodigo: visitaData.iso || '',
              isoTamano: visitaData.tamano,
              isoTipo: visitaData.tipo || '',
              noHacerAltaLibre: false,
              tieneCertificadoImo: false,
              notificacionImo: false,
            };

            const bodyVacioImport = {
              visitas: [visita],
              codigoArmador: visitaData.armador,
              tipoDeDocumento: 'C',
              esProforma: false,
              rutTitular,
              liquidando: true,
              lavado: '',
              oidLiquidacion: id || 0,
            };

            await addContainerBlVacioImport(bodyVacioImport);
            res = { total: 0, detalle: 'VacioImportacion container added' };
          } else {
            res = isImport
              ? await getLiquidateContainerBl(body)
              : await getLiquidateContainerBlExport(bodyExport);
          }

          if (res?.warning) {
            showMessage({
              severity: 'error',
              detail: '',
              summary: `${res?.detalle?.split(' - ')[0]} - ${res?.warning} `,
            });
            warningFound = true;
            return;
          }

          results.push(res);
        })
      );

      if (!warningFound) {
        toggleIsPaymentDialog();
      }
    },
    [isImport, showMessage, toggleIsPaymentDialog, tipo, id]
  );

  const clientFormButtonRef = useRef<HTMLButtonElement>(null);
  const despachanteFormButtonRef = useRef<HTMLButtonElement>(null);

  const getTipoLiquidacion = (): LiquidacionWebTipo => {
    if (tipo === 'VacioImportacion') {
      return 'VacíoImportación';
    }
    return isImport ? 'Importación' : 'Exportación';
  };

  const tipoLiquidacion = getTipoLiquidacion();

  const options = [
    {
      name: t('importFCL:delete'),
      value: 'delete',
      icon: faTrash,
      color: 'danger',
    },
  ];

  const { mutate: saveData, isPending: savingData } = useMutation({
    mutationFn: saveAndAddSettlement,
    onSuccess: (settlementId) => {
      setId(settlementId);
      const newPath = `${location.pathname}?id=${settlementId}`;
      navigate(newPath, { replace: true });
      showToast({
        severity: 'success',
        detail: t('importFCL:settlementSaved'),
      });
    },
    onError: () => {
      showToast({
        severity: 'error',
        detail: t('importFCL:settlementNotSaved'),
      });
    },
  });

  const { mutate: deleteSettlement, isPending: deletingSettlement } =
    useMutation({
      mutationFn: deleteSettlementByOID,
      onSuccess: () => {
        showToast({
          severity: 'success',
          detail: t('importFCL:settlementDeleted'),
        });
        queryClient.invalidateQueries({
          queryKey: ['depo20Client', id],
        });
        navigate(PagesPaths.mySettlement);
      },
      onError: () => {
        showToast({
          severity: 'error',
          detail: t('importFCL:settlementNotDeleted'),
        });
      },
    });

  const { mutate: schedule, isPending: scheduling } = useMutation({
    mutationFn: scheduleImportFCL,
    onSuccess: () => {
      navigate(PagesPaths.mySettlement);
    },
  });

  const handleDropdownChange = (e: { value: string | null }, name: string) => {
    if (e.value === 'print' && name === 'userDropdown') {
      navigate(PagesPaths.Settings);
    }
    if (e.value === 'delete') {
      deleteSettlement({ oidLiquidacion: [id || 0] });
    }
    return null;
  };

  const getStatusColor = (estado?: EstadoType): string => {
    return estado ? statusColors[estado] : 'defaultColor';
  };

  const handleSelectionChange = (e: { value: unknown }) => {
    setSelectedItems(e.value as []);
  };

  const { data, isLoading: formDataLoading } = useQuery<Depo20FormData>({
    enabled: !!id,
    queryKey: ['depo20Client', id],
    queryFn: () => getDepo20List(id || 0),
  });

  const { data: items = [], isLoading: itemsLoading } = useQuery({
    enabled: !!id && !addingContainer,
    queryKey: ['getImportFCLTable', id],
    queryFn: () => getImportFCLTable(id || 0),
  });

  const { data: sitioTextoAgenda } = useQuery({
    queryKey: ['getParametroTexto', 'SITIO_TEXTO_AGENDA_WEB'],
    queryFn: () => getParametroTexto({ nombre: 'SITIO_TEXTO_AGENDA_WEB' }),
  });

  const { mutate: calculateAmountsMutation, isPending: calculatingAmounts } =
    useMutation({
      mutationFn: (
        dataArray: Array<{
          oidVisita: number;
          entregaDirecta: boolean;
          fechaRetiro: string | null;
          fechaIngreso: string | null;
          lateArrival: boolean;
        }>
      ) => calculateAmounts(dataArray, items),
      onError: (error) => {
        clearMessages();
        clearMessages();
        showMessageError(error?.message);
      },
    });

  useEffect(() => {
    const checkAndDeleteSettlement = async () => {
      if (
        !formDataLoading &&
        !itemsLoading &&
        id &&
        !isPaymentDialogShown &&
        !scheduleModalOpen &&
        !savingData &&
        !addingContainer &&
        !deletingSettlement &&
        !isZeroCostDialogOpen
      ) {
        const latestItems = await getImportFCLTable(id);
        if (latestItems.length === 0) {
          deleteSettlement({ oidLiquidacion: [id] });
        }
      }
    };

    checkAndDeleteSettlement();
  }, [
    items,
    id,
    formDataLoading,
    isPaymentDialogShown,
    scheduleModalOpen,
    itemsLoading,
    deleteSettlement,
    showToast,
    t,
    savingData,
    addingContainer,
    deletingSettlement,
    isZeroCostDialogOpen,
  ]);

  useEffect(() => {
    const idParam = params.get('id');
    const disabledParam = params.get('disabled');
    if (idParam) {
      const idParsed = parseInt(idParam, 10);
      setId(idParsed);
    }

    if (disabledParam === 'true' || data?.estado === 'Agendada') {
      setIsDisabled(true);
    }

    const noAccesibleStates = ['Paga', 'Pendiente de Pago'];

    if (noAccesibleStates.includes(data?.estado || '')) {
      navigate(PagesPaths.mySettlement);
    }
  }, [setId, params, data, navigate]);

  const onClientDataChange = (formData: ClientState) => {
    setClientData(formData);
  };

  const onScheduleButtonClick = () => {
    const body = {
      oidLiquidacion: id || 0,
      rut: clientData?.rut || '',
      containers: selectedItems,
    };
    const containersInvalid = selectedItems?.some(({ fechaRetiro }) => {
      const date = new Date(fechaRetiro);
      const currentDate = new Date();
      date.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);
      return date <= currentDate;
    });

    if (containersInvalid) {
      return showToast({
        severity: 'error',
        detail: t('importFCL:containersInvalidDate'),
      });
    }

    return schedule(body);
  };

  const clientValue = useMemo(
    () => ({
      nombre: data?.cliente || '',
      rut: data?.rutCliente || '',
      referencia: data?.observaciones || '',
    }),
    [data]
  );

  const despachanteValue = useMemo(
    () => ({
      nombre: data?.despachante || '',
      rut: data?.rutDespachante || '',
      referencia: data?.observaciones || '',
    }),
    [data]
  );

  const bodyOnSave: SaveSettlementValidated = {
    oid: id || 0,
    consumidorFinal: false,
    tipo: (data?.tipo as LiquidacionWebTipo) || tipoLiquidacion,
    observaciones: isImport
      ? despachanteData?.referencia || ''
      : clientData?.referencia || '',
    cliente: clientData?.nombre || '',
    rutCliente: clientData?.rut || '',
    fechaInicio: data?.fechaInicio || new Date().toISOString(),
    despachante: despachanteData?.nombre || '',
    rutDespachante: despachanteData?.rut || '',
  };

  const { mutate: mutateVerifyRut, isPending: rutLoading } = useMutation({
    mutationFn: verifyRut,
    onSuccess: (response) => {
      const nombre =
        response?.m_Item1?.razonSocial ||
        response?.m_Item1?.nombreFantasia ||
        '';

      if (response?.m_Item1 === null && response?.m_Item2) {
        showMessageError(response?.m_Item2, '');
      }
      setClientData({
        nombre,
        rut: clientData?.rut || '',
        referencia: clientData?.referencia || '',
      });

      return saveData(bodyOnSave);
    },
    onError: () => {},
  });

  const { mutate: mutateTitularRut, isPending: rutTitularLoading } =
    useMutation({
      mutationFn: titularRut,
      onSuccess: (response) => {
        if (Object.keys(response).length) {
          const body = {
            oid: id || 0,
            consumidorFinal: false,
            tipo: (data?.tipo as LiquidacionWebTipo) || tipoLiquidacion,
            observaciones: isImport
              ? despachanteData?.referencia || ''
              : clientData?.referencia || '',
            cliente: response?.nombre || '',
            rutCliente: response?.ruc || '',
            fechaInicio: data?.fechaInicio || new Date().toISOString(),
            despachante: despachanteData?.nombre || '',
            rutDespachante: despachanteData?.rut || '',
          };
          setClientData({
            nombre: response?.nombre || '',
            rut: response?.ruc || '',
            referencia: clientData?.referencia || '',
          });
          saveData(body);
        } else {
          mutateVerifyRut({ rut: clientData?.rut ?? '' });
        }
      },
      onError: () => {
        showToast({
          severity: 'error',
          detail: `Titular no existe`,
        });
      },
    });

  useEffect(() => {
    const newSlectedItems = selectedItems?.filter(({ oid }) =>
      items?.some((item) => item?.oid === oid)
    );

    if (newSlectedItems.length !== selectedItems.length) {
      setSelectedItems(newSlectedItems);
    }
  }, [selectedItems, items]);

  const comprobarDocumentos = async (rut: string, prefix: string) => {
    const response = await comprobarDocumento({
      documento: rut,
      tipo: 2,
    });
    if (response === 1) {
      showToast({
        severity: 'error',
        detail: `${prefix}: El RUT ingresado pertenece a un titular sin dirección. En el proceso de factura electrónica es necesario que el titular contenga el dato mencionado. Comuníquese con atención al cliente para regularizar la información`,
      });
      return false;
    }

    if (response === 2) {
      showToast({
        severity: 'error',
        detail: `${prefix}: El RUT ingresado es inválido`,
      });
      return false;
    }
    return true;
  };

  const validRut = async () => {
    if (
      !clientData?.rut?.match(/^\d{12}$/) ||
      (isImport && !despachanteData?.rut?.match(/^\d{12}$/))
    ) {
      showToast({
        severity: 'error',
        detail: 'El RUT ingresado es inválido',
      });
      return false;
    }

    const resultCliente = await comprobarDocumentos(clientData.rut, 'Cliente');
    if (!resultCliente) return false;

    if (!despachanteData || tipo === 'VacioImportacion') return true;

    const resultDespachante = await comprobarDocumentos(
      despachanteData.rut,
      'Despachante'
    );

    if (!resultDespachante) return false;

    return true;
  };

  const onSaveSettlement = () => {
    clientFormButtonRef?.current?.click();
    despachanteFormButtonRef?.current?.click();

    if (id && id !== 0) {
      if (items.length === 0 && !addingContainer && !savingData) {
        showToast({
          severity: 'warn',
          detail: t('importFCL:noContainersDeleteWarning'),
        });
        deleteSettlement({ oidLiquidacion: [id || 0] });
        return;
      }
    }

    if (!id || id === 0) {
      if (items.length === 0) {
        showToast({
          severity: 'error',
          detail: t('importFCL:emptyContainers'),
        });
        return;
      }
    }

    if (!clientData?.rut?.match(/^\d{12}$/)) {
      showToast({
        severity: 'error',
        detail: t('importFCL:rutInvalid'),
      });
      return;
    }

    if (isImport && !despachanteData?.rut?.match(/^\d{12}$/)) {
      showToast({
        severity: 'error',
        detail: t('importFCL:rutInvalid'),
      });
      return;
    }

    if (!clientData?.nombre) {
      mutateTitularRut({ rut: clientData?.rut || '' });
      return;
    }

    saveData(bodyOnSave);
  };

  return (
    <Container className={styles.containerList}>
      {data && isPaymentDialogShown && (
        <ModalDetailPayment
          oid={id}
          isDialogShown={isPaymentDialogShown}
          toggleDialog={toggleIsPaymentDialog}
          formClientData={data}
          formDataValues={clientData}
          fetchAddLiquidacionLoading={savingData}
          saveLiquidacion={onSaveSettlement}
          tipoLiquidacion={tipoLiquidacion}
          importes={items}
          esProformaWebBody={bodyOnSave}
        />
      )}
      {(savingData || rutLoading || rutTitularLoading) && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="flex flex-column md:flex-row justify-between align-items-center border-b border-gray-300">
        <div className="col-12 md:col-7">
          <Link
            to={PagesPaths.mySettlement}
            className={`${styles.breadcrumb} hover:text-blue-800 w-100`}
          >
            <FontAwesomeIcon icon={faChevronLeft} />

            {t('importFCL:backToSettlements')}
          </Link>
          <h1 className={classNames('mt-2 mb-0', styles.title)}>
            {getPageTitle(id, tipo, isImport, t)}
          </h1>
        </div>
        <div className="col-12 md:col-5 flex gap-3 justify-content-end items-start">
          <div className=" flex flex-wrap gap-3">
            <Button
              className={`${styles.paymentButton} w-auto px-3 gap-2`}
              onClick={() => {
                clearMessages();
                calculateAmountsMutation(items);
              }}
              loading={calculatingAmounts}
              disabled={items.length === 0}
            >
              <FontAwesomeIcon icon={faDollar} />
              {t('importFCL:paymentButton')}
            </Button>
            {isImport && (
              <Button
                className={`${styles.paymentButton} w-auto px-3 gap-2`}
                disabled={
                  !clientData?.rut ||
                  !selectedItems?.length ||
                  scheduling ||
                  isDisabled
                }
                onClick={onScheduleButtonClick}
              >
                <FontAwesomeIcon icon={faBookmark} />
                {t('importFCL:scheduleButton')}
              </Button>
            )}
          </div>

          <div className={styles.user}>
            <DropdownField
              id="userDropdown"
              options={options}
              itemTemplate={optionTemplate}
              onChange={(e) => handleDropdownChange(e, 'userDropdown')}
              className="Depo20DropdownField"
              disabled={notDeletableStates.includes(data?.estado || '')}
              isLoading={deletingSettlement}
            />
          </div>
        </div>
      </div>
      <div
        className={`${styles.status} flex flex-row align-items-center justify-content-end`}
      >
        <span className={styles.bgStatus}>
          <span
            className={styles.ball}
            style={{
              backgroundColor: getStatusColor(data?.estado),
            }}
          />
          {data?.estado || 'En edición'}
        </span>
      </div>
      <Messages ref={elementRef} />

      <div className="flex flex-column md:flex-row align-items-start">
        <aside className={classNames(' col-12 p-0 md:col-4')}>
          <header className="flex justify-content-end md:justify-content-between align-items-center py-2 flex-wrap md:flex-nowrap">
            <div className=" w-full md:w-auto">
              {isImport && (
                <Button
                  secondary
                  className="px-3"
                  style={{ width: 'fit-content', height: 40 }}
                  onClick={() => setScheduleModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faBook} className="mr-2" />
                  {t('importFCL:scheduleDetail')}
                </Button>
              )}
            </div>

            <button
              type="button"
              className={classNames(styles.secondaryActionsButtons)}
              onClick={() =>
                navigate(`${PagesPaths.CustomerRegistration}?from=importFCL`)
              }
            >
              <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
              {t('importFCL:addClient')}
            </button>
          </header>
          <div>
            <ImportFCLClientForm
              titleClient
              formDataLoading={formDataLoading}
              onChange={onClientDataChange}
              value={clientValue}
              buttonRef={clientFormButtonRef}
              isImport={isImport}
              disabled={isDisabled}
            />
          </div>
          {isImport && (
            <div>
              <ImportFCLClientForm
                formDataLoading={formDataLoading}
                value={despachanteValue}
                onChange={(formData) => setDespachanteData(formData)}
                buttonRef={despachanteFormButtonRef}
                disabled={isDisabled}
              />
            </div>
          )}
          {isImport && (
            <p className="text-xs py-1" style={{ color: '#737376' }}>
              {sitioTextoAgenda || ''}
            </p>
          )}
          <div className="flex justify-content-end pt-5">
            <Button
              className={`${styles.paymentButton} w-auto px-3 gap-2`}
              secondary
              onClick={onSaveSettlement}
              disabled={isDisabled}
            >
              <FontAwesomeIcon icon={faSave} />
              {t('importFCL:saveButton')}
            </Button>
          </div>
        </aside>
        <section className={`${styles.tableSection} col-12 md:col-8`}>
          <header className="flex flex-col gap-3 justify-between items-center py-2 mb-3">
            <div className="w-full flex justify-content-between flex-column gap-2 md:flex-row">
              <h2 className="text-xl font-semibold text-blue-800">
                {t('importFCL:sectionTitle')}
              </h2>
              <p
                className="text-sm py-1 font-semibold"
                style={{ color: 'var(--main-color)' }}
              >
                {t('importFCL:addedInfo', { count: selectedItems?.length })}
              </p>
            </div>
            <ToolBar
              isImport={isImport}
              oidLiquidacion={id || 0}
              disabled={isDisabled}
              bodyOnSave={bodyOnSave}
              validRut={validRut}
              rut={clientData ? clientData.rut : ''}
              onLoadingChange={setAddingContainer}
              onZeroCostDialogChange={handleZeroCostDialogChange}
              tipo={tipo}
            />
          </header>
          <div>
            <div
              className={classNames(
                'p-3 rounded-t-md',
                styles.secondaryActionsWrapper
              )}
            >
              {!isDisabled && (
                <DeleteImportFCL
                  items={selectedItems?.map(({ oid }) => oid)}
                  disabled={!selectedItems?.length}
                  textButton={t('importFCL:removeContainersButton')}
                />
              )}

              <b
                className={`hidden md:flex ${classNames(styles.containersSelected)}`}
              >
                {selectedItems?.length
                  ? `${t('importFCL:containersSelected')}: ${selectedItems?.length} `
                  : t('importFCL:noContainerSelected')}
              </b>
            </div>
            <ImportFCLTable
              selectedItems={selectedItems}
              handleSelectionChange={handleSelectionChange}
              data={items}
              isLoading={false}
              tipo={tipo}
              disabled={isDisabled}
            />
          </div>
        </section>
        <ScheduleModalImportFCL
          visible={scheduleModalOpen}
          setVisibility={setScheduleModalOpen}
        />
      </div>
    </Container>
  );
};

export default ImportFCL;
